import { useInfiniteQuery } from "@tanstack/react-query"
import { format, isToday, isYesterday } from "date-fns"
import { ru } from "date-fns/locale/ru"
import { AnimatePresence, motion } from "framer-motion"
import { ScrollText, X } from "lucide-react"
import type { ReactNode } from "react"
import { useSearchParams } from "react-router-dom"
import {
	AUDIT_EVENT_LABELS,
	AUDIT_SERVICES,
	AuditEntryRow,
	type AuditLevel,
	type AuditLogEntry,
	type AuditLogFilters,
	type AuditPerson,
	auditLogKeys,
	listAuditLogs,
} from "@/entities/audit-log"
import { useSessionRole } from "@/entities/session"
import { fetchingDim, fadeIn } from "@/shared/lib/motion"
import { useShownKey } from "@/shared/lib/use-shown-key"
import { cn } from "@/shared/lib/utils"
import { AccessDenied } from "@/shared/ui/access-denied"
import { Button } from "@/shared/ui/button"
import { EmptyState } from "@/shared/ui/empty-state"
import { LoadErrorState } from "@/shared/ui/load-error-state"
import { NativeSelect } from "@/shared/ui/native-select"
import { PageShell } from "@/shared/ui/page-shell"
import { LoadSwap } from "@/shared/ui/load-swap"
import { Skeleton } from "@/shared/ui/skeleton"
import { SlidingPill } from "@/shared/ui/sliding-pill"

const PERIODS = [
	{ value: "", label: "Всё время", days: null },
	{ value: "1", label: "Сегодня", days: 0 },
	{ value: "7", label: "7 дней", days: 7 },
	{ value: "30", label: "30 дней", days: 30 },
] as const

const LEVELS: Array<{ value: "" | AuditLevel; label: string }> = [
	{ value: "", label: "Все" },
	{ value: "warn", label: "Внимание" },
	{ value: "error", label: "Тревоги" },
]

function toFilters(params: URLSearchParams): AuditLogFilters {
	const period = PERIODS.find((item) => item.value === (params.get("period") ?? ""))
	let from: string | undefined
	if (period && period.days !== null) {
		const start = new Date()
		start.setHours(0, 0, 0, 0)
		start.setDate(start.getDate() - period.days)
		from = start.toISOString()
	}
	return {
		service: params.get("service") || undefined,
		event: params.get("event") || undefined,
		level: (params.get("level") as AuditLevel | null) || undefined,
		userId: params.get("userId") || undefined,
		actorId: params.get("actorId") || undefined,
		from,
	}
}

function dayTitle(date: Date): string {
	if (isToday(date)) return "Сегодня"
	if (isYesterday(date)) return "Вчера"
	return format(date, "d MMMM, EEEE", { locale: ru })
}

function byDay(
	entries: AuditLogEntry[],
): Array<{ key: string; title: string; entries: AuditLogEntry[] }> {
	const groups = new Map<string, { key: string; title: string; entries: AuditLogEntry[] }>()
	for (const entry of entries) {
		const date = new Date(entry.createdAt)
		const key = format(date, "yyyy-MM-dd")
		const group = groups.get(key) ?? { key, title: dayTitle(date), entries: [] }
		group.entries.push(entry)
		groups.set(key, group)
	}
	return [...groups.values()]
}

function Segmented<T extends string>({
	label,
	items,
	value,
	onChange,
}: {
	label: string
	items: ReadonlyArray<{ value: T; label: string }>
	value: T
	onChange: (value: T) => void
}) {
	return (
		<div
			role="tablist"
			aria-label={label}
			className="hide-scrollbar relative flex max-w-full gap-1 overflow-x-auto rounded-full bg-secondary/60 p-1"
		>
			<SlidingPill className="rounded-full" />
			{items.map((item) => {
				const active = item.value === value
				return (
					<button
						key={item.value || "all"}
						type="button"
						role="tab"
						aria-selected={active}
						data-active={active}
						onClick={() => onChange(item.value)}
						className={cn(
							"relative shrink-0 rounded-full px-3 py-1.5 text-sm whitespace-nowrap transition-colors",
							active ? "text-foreground" : "text-muted-foreground hover:text-foreground",
						)}
					>
						<span className="relative">{item.label}</span>
					</button>
				)
			})}
		</div>
	)
}

function PersonFilter({
	label,
	person,
	onClear,
}: {
	label: string
	person: AuditPerson | undefined
	onClear: () => void
}): ReactNode {
	return (
		<span className="inline-flex items-center gap-1 rounded-full bg-primary/15 py-1 pr-1 pl-3 text-sm ring-1 ring-primary/40">
			<span className="text-muted-foreground">{label}:</span>
			<span className="font-medium">{person?.name ?? "выбранный"}</span>
			<button
				type="button"
				onClick={onClear}
				aria-label={`Убрать фильтр «${label}»`}
				className="grid size-6 place-items-center rounded-full hover:bg-background/40"
			>
				<X className="size-3.5" />
			</button>
		</span>
	)
}

/** Заглушка ленты: заголовок дня и строки со временем, значком и двумя строками текста. */
function AuditSkeleton() {
	return (
		<div aria-hidden="true" className="space-y-6">
			{[5, 4].map((rows, group) => (
				<section key={group}>
					<div className="mb-1 flex h-8 items-center">
						<Skeleton className="h-3 w-28 rounded" />
					</div>
					<ul className="space-y-0.5">
						{Array.from({ length: rows }, (_, index) => (
							<li key={index} className="flex items-start gap-3 px-3 py-2.5">
								<Skeleton className="mt-1 h-3 w-9 shrink-0 rounded" />
								<Skeleton className="size-7 shrink-0 rounded-lg" />
								<div className="min-w-0 flex-1">
									<div className="flex h-[19px] items-center">
										<Skeleton
											className="h-3.5 rounded"
											style={{ width: `${58 - ((index * 13) % 28)}%` }}
										/>
									</div>
									<div className="mt-0.5 flex h-4 items-center">
										<Skeleton className="h-3 w-40 rounded" />
									</div>
								</div>
								<Skeleton className="mt-1 size-4 shrink-0 rounded" />
							</li>
						))}
					</ul>
				</section>
			))}
		</div>
	)
}

/** Журнал действий: лента по дням, где вместо идентификаторов стоят имена людей. */
export function AuditLogPage() {
	const { isAdmin } = useSessionRole()
	const [params, setParams] = useSearchParams()
	const filters = toFilters(params)
	const service = params.get("service") ?? ""
	const category = AUDIT_SERVICES.find((item) => item.value === service)

	const log = useInfiniteQuery({
		queryKey: auditLogKeys.list(filters),
		queryFn: ({ pageParam }) => listAuditLogs(filters, pageParam),
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (page) => page.nextCursor ?? undefined,
		enabled: isAdmin,
		placeholderData: (previous) => previous,
	})
	// Переход на смену фильтра идёт, когда пришли новые записи: до этого видна прежняя лента.
	const shownKey = useShownKey(params.toString(), log.isPlaceholderData)

	if (!isAdmin) return <AccessDenied title="Журнал" />

	const set = (patch: Record<string, string>) => {
		const next = new URLSearchParams(params)
		for (const [key, value] of Object.entries(patch)) {
			if (value) next.set(key, value)
			else next.delete(key)
		}
		setParams(next, { replace: true })
	}

	const entries = log.data?.pages.flatMap((page) => page.entries) ?? []
	const people = Object.assign(
		{},
		...(log.data?.pages.map((page) => page.people ?? {}) ?? []),
	) as Record<string, AuditPerson>
	const events = Object.entries(AUDIT_EVENT_LABELS).filter(
		([event]) => !category || category.prefixes.some((prefix) => event.startsWith(`${prefix}.`)),
	)
	const filtered = [...params.keys()].length > 0

	return (
		<PageShell
			eyebrow="Безопасность"
			title="Журнал"
			subtitle="Кто и что делал: входы, права, публикации, боты и рассылки. Нажмите на имя, чтобы увидеть только его действия."
		>
			<div className="mb-6 space-y-3">
				<div className="flex flex-wrap items-center gap-3">
					<Segmented
						label="Раздел"
						items={[
							{ value: "", label: "Всё" },
							...AUDIT_SERVICES.map(({ value, label }) => ({ value, label })),
						]}
						value={service}
						onChange={(value) => set({ service: value, event: "" })}
					/>
					<Segmented
						label="Период"
						items={PERIODS}
						value={(params.get("period") ?? "") as (typeof PERIODS)[number]["value"]}
						onChange={(value) => set({ period: value })}
					/>
				</div>
				<div className="flex flex-wrap items-center gap-3">
					<Segmented
						label="Важность"
						items={LEVELS}
						value={(params.get("level") ?? "") as "" | AuditLevel}
						onChange={(value) => set({ level: value })}
					/>
					<NativeSelect
						aria-label="Событие"
						value={params.get("event") ?? ""}
						onChange={(event) => set({ event: event.target.value })}
						className="h-10 w-auto min-w-52 rounded-full"
					>
						<option value="">Любое событие</option>
						{events.map(([value, label]) => (
							<option key={value} value={value}>
								{label}
							</option>
						))}
					</NativeSelect>
					{/* Фильтры по людям и «Сбросить всё» проявляются и гаснут, а не возникают рывком. */}
					<AnimatePresence initial={false}>
						{params.get("actorId") ? (
							<motion.span
								key="actor"
								variants={fadeIn}
								initial="hidden"
								animate="visible"
								exit="exit"
							>
								<PersonFilter
									label="Кто"
									person={people[params.get("actorId")!]}
									onClear={() => set({ actorId: "" })}
								/>
							</motion.span>
						) : null}
						{params.get("userId") ? (
							<motion.span
								key="user"
								variants={fadeIn}
								initial="hidden"
								animate="visible"
								exit="exit"
							>
								<PersonFilter
									label="Над кем"
									person={people[params.get("userId")!]}
									onClear={() => set({ userId: "" })}
								/>
							</motion.span>
						) : null}
						{filtered ? (
							<motion.span
								key="reset"
								variants={fadeIn}
								initial="hidden"
								animate="visible"
								exit="exit"
							>
								<Button variant="ghost" size="sm" onClick={() => setParams({}, { replace: true })}>
									Сбросить всё
								</Button>
							</motion.span>
						) : null}
					</AnimatePresence>
				</div>
			</div>

			<LoadSwap
				loading={log.isPending}
				id={log.isError ? "error" : entries.length ? `list-${shownKey}` : `empty-${shownKey}`}
				className={fetchingDim(log.isFetching && log.isPlaceholderData)}
			>
				{log.isPending ? (
					<AuditSkeleton />
				) : log.isError ? (
					<LoadErrorState onRetry={() => void log.refetch()} />
				) : entries.length === 0 ? (
					<EmptyState icon={ScrollText} title="Записей нет">
						{filtered ? "Под эти фильтры ничего не попало." : "Журнал пока пуст."}
					</EmptyState>
				) : (
					<div className="space-y-6">
						<AnimatePresence initial={false}>
							{byDay(entries).map((group) => (
								<motion.section
									key={group.key}
									variants={fadeIn}
									initial="hidden"
									animate="visible"
								>
									<h2 className="sticky top-[53px] z-10 -mx-1 mb-1 bg-background/90 px-1 py-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase backdrop-blur lg:top-0">
										{group.title}
										<span className="ml-2 font-normal normal-case tabular-nums">
											{group.entries.length}
										</span>
									</h2>
									{/* Записи, догруженные кнопкой «Показать ещё», проявляются, а не возникают. */}
									<ul className="space-y-0.5">
										<AnimatePresence initial={false}>
											{group.entries.map((entry) => (
												<AuditEntryRow
													key={entry.id}
													entry={entry}
													people={people}
													onPickUser={(userId) => set({ userId })}
													onPickActor={(actorId) => set({ actorId })}
												/>
											))}
										</AnimatePresence>
									</ul>
								</motion.section>
							))}
						</AnimatePresence>
						<AnimatePresence initial={false}>
							{log.hasNextPage ? (
								<motion.div
									key="more"
									variants={fadeIn}
									initial="hidden"
									animate="visible"
									exit="exit"
									className="flex justify-center"
								>
									<Button
										variant="secondary"
										onClick={() => void log.fetchNextPage()}
										isLoading={log.isFetchingNextPage}
									>
										Показать ещё
									</Button>
								</motion.div>
							) : null}
						</AnimatePresence>
					</div>
				)}
			</LoadSwap>
		</PageShell>
	)
}
